import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { EndeavorApi, EndeavorMetadataApi, EndeavorTypeApi } from '../../services/ApiService';
import { useToast } from '../../contexts/ToastContext';
import { useData } from '../../contexts/DataContext';
import './CourseDetails.css';

/**
 * CourseDetails component for inline editing of course/endeavor information
 * Two-column layout: Left (title + description), Right (details box with fields)
 */
function CourseDetails({ endeavor, onUpdate, onClose, events }) {
  const [editedData, setEditedData] = useState({});
  const [editingField, setEditingField] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [fullEndeavor, setFullEndeavor] = useState(endeavor);
  const [loading, setLoading] = useState(false);
  const [metadataDefinitions, setMetadataDefinitions] = useState(null);
  const [sharingCourse, setSharingCourse] = useState(false);
  const { addToast } = useToast();
  const navigate = useNavigate();
  
  // Get setCachedData from DataContext (may not be available in all contexts)
  let setCachedData;
  try {
    const dataContext = useData();
    setCachedData = dataContext?.setCachedData;
  } catch (e) {
    console.warn('[CourseDetails] useData not available:', e);
    setCachedData = null;
  }

  // Metadata field mapping: UI field name → metadata name in backend
  const METADATA_FIELDS = {
    courseCode: 'Course Code',
    professor: 'Professor',
    room: 'Course Location',
    dates: 'Course Dates',
    officeHours: 'Office Hours'
  };

  // Entity fields that go directly to the endeavor table
  const ENTITY_FIELDS = ['title', 'description'];

  // Find metadata entry by name (check all possible property paths)
  const findMetadataByName = (metadataName) => {
    const lower = metadataName.toLowerCase();
    return fullEndeavor?.metadata?.find(m => {
      const n1 = (m.metadataDefinition?.name || '').toLowerCase();
      const n2 = (m.metadataName || '').toLowerCase();
      const n3 = (m.metadata_name || '').toLowerCase();
      const n4 = (m.name || '').toLowerCase();
      return n1 === lower || n2 === lower || n3 === lower || n4 === lower;
    });
  };

  // Validate before save
  const validateData = (data) => {
    const errors = {};

    // Title required and max 100 chars
    if ('title' in data) {
      const title = data.title?.trim() || '';
      if (!title) {
        errors.title = 'Title is required';
      } else if (title.length > 100) {
        errors.title = 'Title must be 100 characters or less';
      }
    }

    // CourseCode max 150 chars
    if ('courseCode' in data && data.courseCode) {
      if (data.courseCode.length > 150) {
        errors.courseCode = 'Course code must be 150 characters or less';
      }
    }

    return errors;
  };

  // Custom save handler for entity + metadata fields
  const handleSaveClick = async () => {
    console.log('[CourseDetails] handleSaveClick called, editedData:', editedData);
    
    // Validate
    const errors = validateData(editedData);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      console.error('[CourseDetails] Validation errors:', errors);
      return;
    }

    setSaving(true);
    try {
      const endeavorId = fullEndeavor.endeavorId || fullEndeavor.id;
      let updatedEndeavor = { ...fullEndeavor }; // Start with current data
      
      // Step 1: Update entity fields if any changed
      const entityChanges = {};
      for (const field of ENTITY_FIELDS) {
        if (field in editedData) {
          if (field === 'title') {
            entityChanges.name = editedData.title;
            updatedEndeavor.name = editedData.title;
            updatedEndeavor.title = editedData.title;
          } else {
            entityChanges[field] = editedData[field];
            updatedEndeavor[field] = editedData[field];
          }
        }
      }

      if (Object.keys(entityChanges).length > 0) {
        console.log('[CourseDetails] Updating entity fields:', entityChanges);
        const response = await EndeavorApi.updateEndeavor(endeavorId, entityChanges);
        if (!response.ok) {
          throw new Error('Failed to update course fields');
        }
        // If backend returned updated entity, merge it
        try {
          const serverData = await response.json();
          if (serverData) {
            updatedEndeavor = { ...updatedEndeavor, ...serverData };
          }
        } catch (e) {
          // No response body, use optimistic update
        }
      }

      // Step 2: Update or create metadata fields individually
      const metadataUpdates = [];
      for (const [uiField, metadataName] of Object.entries(METADATA_FIELDS)) {
        if (uiField in editedData) {
          const metadataEntry = findMetadataByName(metadataName);
          if (metadataEntry) {
            // UPDATE existing metadata
            console.log(`[CourseDetails] Updating metadata ${metadataName}:`, editedData[uiField]);
            const promise = EndeavorMetadataApi.updateMetadata(metadataEntry.id, {
              id: metadataEntry.id,
              endeavorId: endeavorId,
              metadataDefinitionId: metadataEntry.metadataDefinitionId,
              value: editedData[uiField] || ''
            }).then(async (response) => {
              // Update the metadata array with the server response
              if (response.ok) {
                const updatedMetadata = await response.json();
                // Update the metadata entry in our local copy
                if (updatedEndeavor.metadata) {
                  updatedEndeavor.metadata = updatedEndeavor.metadata.map(m => 
                    m.id === updatedMetadata.id ? { ...m, ...updatedMetadata } : m
                  );
                }
                // Also update the flattened field on the endeavor
                const fieldMap = {
                  'Course Code': 'courseCode',
                  'Professor': 'professor',
                  'Course Location': 'location',
                  'Course Dates': 'dates',
                  'Office Hours': 'officeHours'
                };
                const flatField = fieldMap[metadataName];
                if (flatField) {
                  updatedEndeavor[flatField] = updatedMetadata.value;
                }
              }
              return response;
            });
            metadataUpdates.push(promise);
          } else {
            // CREATE new metadata - need to find metadataDefinitionId first
            console.log(`[CourseDetails] Metadata entry not found for ${metadataName}, will attempt to create`);
            
            // Look for the metadataDefinitionId from the endeavor type's metadata definitions
            let metadataDefinitionId = null;
            
            if (metadataDefinitions) {
              // metadataDefinitions is an array of EndeavorTypeMetadataDefinition objects
              // Each has: { id, endeavorTypeId, metadataDefinitionId, isRequired, ordering, metadataDefinition: {...} }
              const def = metadataDefinitions.find(md => 
                md.metadataDefinition?.name === metadataName
              );
              metadataDefinitionId = def?.metadataDefinitionId || def?.metadataDefinition?.id;
            }
            
            if (metadataDefinitionId) {
              console.log(`[CourseDetails] Creating metadata ${metadataName} with definitionId ${metadataDefinitionId}`);
              const promise = EndeavorMetadataApi.addMetadata({
                endeavorId: endeavorId,
                metadataDefinitionId: metadataDefinitionId,
                value: editedData[uiField] || ''
              }).then(async (response) => {
                if (response.ok) {
                  const newMetadata = await response.json();
                  // Add the new metadata entry to our local copy
                  if (!updatedEndeavor.metadata) {
                    updatedEndeavor.metadata = [];
                  }
                  updatedEndeavor.metadata.push(newMetadata);
                  
                  // Also update the flattened field
                  const fieldMap = {
                    'Course Code': 'courseCode',
                    'Professor': 'professor',
                    'Course Location': 'location',
                    'Course Dates': 'dates',
                    'Office Hours': 'officeHours'
                  };
                  const flatField = fieldMap[metadataName];
                  if (flatField) {
                    updatedEndeavor[flatField] = newMetadata.value;
                  }
                }
                return response;
              });
              metadataUpdates.push(promise);
            } else {
              console.warn(`[CourseDetails] Cannot create metadata ${metadataName}: metadataDefinitionId not found`);
            }
          }
        }
      }

      if (metadataUpdates.length > 0) {
        await Promise.all(metadataUpdates);
      }

      // Success!
      addToast({
        message: 'Course saved successfully',
        intent: 'success',
        duration: 2000
      });

      // Clear edit state
      setEditedData({});
      setEditingField(null);
      setValidationErrors({});

      // Update local state immediately so component displays new values
      setFullEndeavor(updatedEndeavor);

      // Update cache with the modified endeavor
      if (setCachedData) {
        setCachedData(`endeavor_${endeavorId}`, updatedEndeavor);
      }

      // Call onUpdate to refresh UI with updated data
      if (onUpdate) {
        onUpdate(updatedEndeavor);
      }

    } catch (error) {
      console.error('[CourseDetails] Save failed:', error);
      addToast({
        message: error.message || 'Failed to save changes',
        intent: 'danger',
        duration: 4000
      });
    } finally {
      setSaving(false);
    }
  };

  // Fetch full endeavor with metadata if not present
  useEffect(() => {
    const fetchFullEndeavor = async () => {
      if (!endeavor?.metadata && endeavor?.endeavorId) {
        setLoading(true);
        try {
          const response = await EndeavorApi.getEndeavor(endeavor.endeavorId || endeavor.id);
          if (response.ok) {
            const data = await response.json();
            // Merge: API data first, then overlay original endeavor's flattened fields
            // so card-sourced values (courseCode, professor, etc.) aren't lost
            const merged = { ...data, ...endeavor, metadata: data.metadata || endeavor.metadata };
            setFullEndeavor(merged);
            
            // Also fetch metadata definitions for this endeavor type
            if (data.endeavorTypeId) {
              try {
                const metaDefsResponse = await EndeavorTypeApi.getEndeavorTypeMetadataDefinitions(data.endeavorTypeId);
                if (metaDefsResponse.ok) {
                  const metaDefs = await metaDefsResponse.json();
                  setMetadataDefinitions(metaDefs);
                  console.log('[CourseDetails] Loaded metadata definitions:', metaDefs);
                }
              } catch (error) {
                console.error('[CourseDetails] Failed to fetch metadata definitions:', error);
              }
            }
          }
        } catch (error) {
          console.error('[CourseDetails] Failed to fetch full endeavor:', error);
          setFullEndeavor(endeavor);
        } finally {
          setLoading(false);
        }
      } else {
        setFullEndeavor(endeavor);
        
        // Still try to fetch metadata definitions if we have endeavorTypeId
        if (endeavor?.endeavorTypeId && !metadataDefinitions) {
          try {
            const metaDefsResponse = await EndeavorTypeApi.getEndeavorTypeMetadataDefinitions(endeavor.endeavorTypeId);
            if (metaDefsResponse.ok) {
              const metaDefs = await metaDefsResponse.json();
              setMetadataDefinitions(metaDefs);
              console.log('[CourseDetails] Loaded metadata definitions:', metaDefs);
            }
          } catch (error) {
            console.error('[CourseDetails] Failed to fetch metadata definitions:', error);
          }
        }
      }
    };
    
    fetchFullEndeavor();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endeavor?.endeavorId, endeavor?.id, endeavor?.endeavorTypeId]);

  // Clear edited data when endeavor prop changes
  useEffect(() => {
    setEditedData({});
    setEditingField(null);
    setValidationErrors({});
  }, [endeavor]);

  if (!endeavor) return null;
  if (loading) return <div className="course-details-container">Loading course details...</div>;

  const hasChanges = Object.keys(editedData).length > 0;

  const startEditingField = (field, currentValue) => {
    setEditingField(field);
    // Map location → room for editing
    const editField = field === 'location' ? 'room' : field;
    setEditedData(prev => ({
      ...prev,
      [editField]: currentValue || ''
    }));
  };

  const updateField = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleCancelClick = () => {
    setEditedData({});
    setEditingField(null);
    setValidationErrors({});
  };

  // Helper to get metadata value by field name, with fallback to flattened field
  const getMetadataValue = (fieldName) => {
    const metadataName = METADATA_FIELDS[fieldName];
    if (!metadataName) return '';
    const entry = findMetadataByName(metadataName);
    // Fallback to flattened field if metadata not found (for data from card dataset)
    return entry?.value || fullEndeavor[fieldName] || '';
  };

  // Robust course code resolver — mirrors CoursesColumn's extraction logic
  const resolveCourseCode = () => {
    // 1. Check metadata for "Course Code" or "Code"
    const fromMeta = findMetadataByName('Course Code')?.value
      || findMetadataByName('Code')?.value;
    if (fromMeta) return fromMeta;

    // 2. Check flattened fields on the endeavor object
    const fromFlat = fullEndeavor.courseCode || fullEndeavor.code || fullEndeavor.shortName || '';
    if (fromFlat) return fromFlat;

    // 3. Check the original prop (has card-sourced data)
    const fromProp = endeavor?.courseCode || endeavor?.code || endeavor?.shortName || '';
    if (fromProp) return fromProp;

    // 4. Regex extract from title (same pattern as CoursesColumn)
    const title = fullEndeavor.title || fullEndeavor.name || '';
    if (title) {
      const m = title.match(/^\s*([A-Za-z]{1,6}\s?-?\d{2,6}(?:\.\d+)?)/);
      if (m && m[1]) return m[1].trim();
    }

    return '';
  };

  // Derive course date range from actual events (earliest → latest)
  const computeDateRange = () => {
    const evts = events || window.scoutiqEffectiveEvents || [];
    if (!evts.length) return '';
    const timestamps = evts
      .map(e => {
        const raw = e.startDatetime || e.eventDate || e.startDate || e.dueDate || e.date || '';
        if (!raw) return null;
        const d = new Date(raw);
        return isNaN(d.getTime()) ? null : d.getTime();
      })
      .filter(Boolean);
    if (!timestamps.length) return '';
    const earliest = new Date(Math.min(...timestamps));
    const latest = new Date(Math.max(...timestamps));
    const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    // If same day, just show one date
    if (earliest.toDateString() === latest.toDateString()) return fmt(earliest);
    return `${fmt(earliest)} – ${fmt(latest)}`;
  };

  // Extract display values
  const displayTitle = editedData.title !== undefined ? editedData.title : (fullEndeavor.title || fullEndeavor.name || '');
  const displayDescription = editedData.description !== undefined ? editedData.description : (fullEndeavor.description || '');
  const displayCourseCode = editedData.courseCode !== undefined ? editedData.courseCode : resolveCourseCode();
  const displayProfessor = editedData.professor !== undefined ? editedData.professor : getMetadataValue('professor');
  const displayRoom = editedData.room !== undefined ? editedData.room : (getMetadataValue('room') || fullEndeavor.location);
  const displayDates = computeDateRange() || getMetadataValue('dates');
  const displayOfficeHours = editedData.officeHours !== undefined ? editedData.officeHours : getMetadataValue('officeHours');

  return (
    <div className="course-details-container">
      {/* Contextual save banner — slides in only when edits exist */}
      {hasChanges && (
        <div className="cd-save-banner">
          <span className="cd-save-banner-text">Unsaved changes</span>
          <div className="cd-save-banner-actions">
            <button
              type="button"
              className="cd-save-banner-btn cd-btn-cancel"
              onClick={handleCancelClick}
              disabled={saving}
            >
              Discard
            </button>
            <button
              type="button"
              className="cd-save-banner-btn cd-btn-save"
              onClick={handleSaveClick}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* Editable title — click to rename */}
      <div className="course-title-section">
        <div className="cd-section-label">Course Title</div>
        {editingField === 'title' ? (
          <input
            type="text"
            className="course-title-input"
            value={displayTitle}
            onChange={(e) => updateField('title', e.target.value)}
            maxLength={100}
            autoFocus
            placeholder="Course title"
          />
        ) : (
          <h2
            className="course-title-embedded clickable"
            onClick={() => startEditingField('title', fullEndeavor.title || fullEndeavor.name || '')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                startEditingField('title', fullEndeavor.title || fullEndeavor.name || '');
              }
            }}
          >
            {displayTitle || 'Untitled Course'}
          </h2>
        )}
        {validationErrors.title && (
          <div className="validation-error">{validationErrors.title}</div>
        )}
      </div>

      {/* Quick actions — open full page, share course */}
      <CourseQuickActions
        endeavor={fullEndeavor}
        sharing={sharingCourse}
        onOpenFull={() => {
          const id = fullEndeavor.endeavorId || fullEndeavor.id;
          if (id) navigate(`/course/${id}`);
        }}
        onOpenAdmin={() => {
          const id = fullEndeavor.endeavorId || fullEndeavor.id;
          if (id) navigate(`/course/${id}/admin`);
        }}
        onShare={async () => {
          const id = fullEndeavor.endeavorId || fullEndeavor.id;
          if (!id || sharingCourse) return;
          setSharingCourse(true);
          try {
            const resp = await EndeavorApi.shareEndeavor(id);
            if (!resp || !resp.ok) {
              const txt = resp && resp.text ? await resp.text() : `status ${resp && resp.status}`;
              addToast({ type: 'error', message: `Failed to share course: ${txt}` });
              return;
            }
            const updated = await resp.json().catch(() => null);
            if (updated) {
              setFullEndeavor(prev => ({ ...prev, ...updated }));
              if (typeof onUpdate === 'function') onUpdate({ ...fullEndeavor, ...updated });
            }
            addToast({ type: 'success', message: 'Course shared. Open the Manage tab to create invite codes.' });
            navigate(`/course/${id}`);
          } catch (err) {
            addToast({ type: 'error', message: `Failed to share course: ${err && err.message ? err.message : err}` });
          } finally {
            setSharingCourse(false);
          }
        }}
      />

      {/* Fluid two-column layout — no boxes, just content */}
      <div className="course-details-body">
        {/* Left: Description */}
        <div className="course-left-column">
          <div className="cd-section-label">Description</div>
          {editingField === 'description' ? (
            <textarea
              className="course-description-input"
              value={displayDescription}
              onChange={(e) => updateField('description', e.target.value)}
              rows={8}
              placeholder="Enter course description"
              autoFocus
            />
          ) : (
            <div
              className="course-description-text clickable"
              onClick={() => startEditingField('description', fullEndeavor.description)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  startEditingField('description', fullEndeavor.description);
                }
              }}
            >
              {displayDescription || 'No description provided'}
            </div>
          )}
        </div>

        {/* Right: Details */}
        <div className="course-right-column">
          <div className="cd-section-label">Details</div>
          <div className="course-details-list">

            {/* Course Code (read-only) */}
            <div className="cd-field">
              <span className="cd-field-label">Course Code</span>
              <span className="cd-field-value">{displayCourseCode || '—'}</span>
            </div>

            {/* Professor */}
            <div className={`cd-field ${editingField === 'professor' ? 'is-editing' : ''}`}>
              <span className="cd-field-label">Professor</span>
              {editingField === 'professor' ? (
                <input
                  type="text"
                  className="cd-field-input"
                  value={displayProfessor || ''}
                  onChange={(e) => updateField('professor', e.target.value)}
                  placeholder="Professor name"
                  autoFocus
                />
              ) : (
                <span
                  className="cd-field-value clickable"
                  onClick={() => startEditingField('professor', getMetadataValue('professor'))}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') startEditingField('professor', getMetadataValue('professor')); }}
                >
                  {getMetadataValue('professor') || '—'}
                </span>
              )}
            </div>

            {/* Room */}
            <div className={`cd-field ${editingField === 'location' ? 'is-editing' : ''}`}>
              <span className="cd-field-label">Room</span>
              {editingField === 'location' ? (
                <textarea
                  className="cd-field-input cd-field-textarea"
                  value={displayRoom || ''}
                  onChange={(e) => updateField('room', e.target.value)}
                  rows={2}
                  placeholder="Room location"
                  autoFocus
                />
              ) : (
                <span
                  className="cd-field-value clickable"
                  onClick={() => startEditingField('location', getMetadataValue('room') || fullEndeavor.location)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') startEditingField('location', getMetadataValue('room') || fullEndeavor.location); }}
                >
                  {getMetadataValue('room') || fullEndeavor.location || '—'}
                </span>
              )}
            </div>

            {/* Course Dates (auto-derived from events) */}
            <div className="cd-field">
              <span className="cd-field-label">Course Dates</span>
              <span className="cd-field-value">{displayDates || '—'}</span>
            </div>

            {/* Office Hours */}
            <div className={`cd-field ${editingField === 'officeHours' ? 'is-editing' : ''}`}>
              <span className="cd-field-label">Office Hours</span>
              {editingField === 'officeHours' ? (
                <textarea
                  className="cd-field-input cd-field-textarea"
                  value={displayOfficeHours || ''}
                  onChange={(e) => updateField('officeHours', e.target.value)}
                  rows={3}
                  placeholder="Office hours schedule"
                  autoFocus
                />
              ) : (
                <span
                  className="cd-field-value clickable"
                  onClick={() => startEditingField('officeHours', getMetadataValue('officeHours'))}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') startEditingField('officeHours', getMetadataValue('officeHours')); }}
                >
                  {getMetadataValue('officeHours') || '—'}
                </span>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export default CourseDetails;

/**
 * Quick-action row shown at the top of the course info panel.
 * - "Open full course page" navigates to /course/:id.
 * - "Share course" promotes a personal course to a student-shared one so the
 *   owner can issue invite codes. Hidden when the course is already shared
 *   or org-owned (the backend would reject the request in those cases anyway).
 */
function CourseQuickActions({ endeavor, sharing, onOpenFull, onOpenAdmin, onShare }) {
  const id = endeavor && (endeavor.endeavorId || endeavor.id);
  const ownershipType = endeavor && endeavor.ownershipType;
  const isShared = ownershipType === 'student_shared' || ownershipType === 'org_owned';
  const canShare = !!id
    && (!ownershipType || ownershipType === 'personal')
    && !isShared;

  if (!id) return null;

  return (
    <div className="cd-quick-actions" role="group" aria-label="Course actions">
      <button
        type="button"
        className="cd-quick-action-btn"
        onClick={onOpenFull}
        title="Open the full course page"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M14 3h7v7" />
          <path d="M10 14L21 3" />
          <path d="M21 14v7H3V3h7" />
        </svg>
        <span>Open full course page</span>
      </button>
      {canShare && (
        <button
          type="button"
          className="cd-quick-action-btn cd-quick-action-btn--primary"
          onClick={onShare}
          disabled={sharing}
          title="Make this course shareable so other students can join with an invite code"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
          </svg>
          <span>{sharing ? 'Sharing…' : 'Share course'}</span>
        </button>
      )}
      {isShared && (
        <button
          type="button"
          className="cd-quick-action-btn cd-quick-action-btn--primary"
          onClick={onOpenAdmin}
          title="Manage this shared course (members, invite codes, settings)"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
          <span>Manage course</span>
        </button>
      )}
    </div>
  );
}
